import {
  RateType,
  SiteRateSchedule_Create,
  SiteRateSchedule_Read,
  SiteRateSchedule_Update,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { isLegacyCommissionRateType } from '~components/PayCommission/commissionPercentageSchema';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { resolveDriverPayRate } from '~hooks/useSiteRateSchedule/driverPayRate';
import { SiteBasic } from '~hooks/useSites/models';
// Import the DTO type from SiteRatesForm
import type { SiteRateScheduleDTO } from '~pages/Sales/Projects/components/SiteRatesForm';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import { parseFiniteNumber } from '~utils/utilFunctions';

// Normalize cleared numeric inputs at the API boundary. react-hook-form's
// `watch()` returns raw field values without the yup `.transform()` that maps
// `""` → `undefined`, so cleared rate inputs reach this layer as empty
// strings. `?? null` does not catch `""`, which would forward an empty string
// to the API and trip Committee's `expected number, but received String: ""`.
const numOrNull = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  return Number(value);
};

const deparseCommissionFields = (
  source: Pick<
    SiteRateScheduleDTO,
    'vendorPayCommissionPercentage' | 'driverPayCommissionPercentage'
  >,
) =>
  ({
    vendor_pay_commission_percentage: source.vendorPayCommissionPercentage,
    driver_pay_commission_percentage: source.driverPayCommissionPercentage,
  }) satisfies Pick<
    SiteRateSchedule_Create,
    'vendor_pay_commission_percentage' | 'driver_pay_commission_percentage'
  >;

const deparseCommissionFieldsDTO = (siteRateDTO: SiteRateScheduleDTO) => {
  const driverPayRate = resolveDriverPayRate(siteRateDTO);
  const percentageForRateType = (
    percentage: string | null | undefined,
    rateType: string | null | undefined,
  ) => (isLegacyCommissionRateType(rateType) && !isNil(percentage) ? null : percentage);

  return deparseCommissionFields({
    vendorPayCommissionPercentage: percentageForRateType(
      siteRateDTO.vendorPayCommissionPercentage,
      siteRateDTO.vendorRate?.type?.value,
    ),
    driverPayCommissionPercentage: percentageForRateType(
      siteRateDTO.driverPayCommissionPercentage,
      driverPayRate?.type?.value,
    ),
  });
};

export class SiteRateSchedule {
  public static parse(proto: SiteRateSchedule_Read): SiteRateSchedule {
    // Fall back to deprecated `site` field for backward compat with older API responses
    const pickupSiteProto = proto.pickup_site ?? proto.site ?? null;
    return new SiteRateSchedule(
      proto.id,
      pickupSiteProto ? SiteBasic.parse(pickupSiteProto) : null,
      proto.dropoff_site ? SiteBasic.parse(proto.dropoff_site) : null,
      proto.customer_rate ? RateBasicWithValue.parse(proto.customer_rate) : null,
      parseFiniteNumber(proto.customer_rate_value),
      proto.customer_rate_type ?? null,
      proto.vendor_rate ? RateBasicWithValue.parse(proto.vendor_rate) : null,
      parseFiniteNumber(proto.vendor_rate_value),
      proto.vendor_rate_type ?? null,
      proto.driver_rate ? RateBasicWithValue.parse(proto.driver_rate) : null,
      parseFiniteNumber(proto.driver_rate_value),
      proto.driver_rate_type ?? null,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.name ?? null,
      proto.material ? ItemNameAndId.parse(proto.material) : null,
      proto.equipment_type ? ItemNameAndId.parse(proto.equipment_type) : null,
      numOrNull(proto.quantity),
      proto.jurisdiction ?? null,
      parseFiniteNumber(proto.pw_on_site_rate),
      parseFiniteNumber(proto.total_fringe_rate),
      proto.pinned_at ? dayjs(proto.pinned_at) : null,
      proto.dereferenced ?? false,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.vendor_pay_commission_percentage ?? null,
      proto.driver_pay_commission_percentage ?? null,
      proto.net_vendor_rate ?? null,
      proto.net_driver_rate ?? null,
      proto.inherited_vendor_pay_commission_percentage ?? null,
      proto.inherited_driver_pay_commission_percentage ?? null,
    );
  }

  public static deparse(siteRateSchedule: SiteRateSchedule): SiteRateSchedule_Create {
    return {
      ...deparseCommissionFields(siteRateSchedule),
      name: siteRateSchedule.name ?? null,
      pickup_site_id: siteRateSchedule.pickupSite?.id ?? null,
      dropoff_site_id: siteRateSchedule.dropoffSite?.id ?? null,
      material_id: siteRateSchedule.material?.id ?? null,
      equipment_type_id: siteRateSchedule.equipmentType?.id ?? null,
      quantity: siteRateSchedule.quantity ?? null,
      customer_rate_id: siteRateSchedule.customerRate?.id ?? null,
      customer_rate_value: siteRateSchedule.customerRateValue ?? null,
      customer_rate_type: siteRateSchedule.customerRateType ?? null,
      vendor_rate_id: siteRateSchedule.vendorRate?.id ?? null,
      vendor_rate_value: siteRateSchedule.vendorRateValue ?? null,
      vendor_rate_type: siteRateSchedule.vendorRateType ?? null,
      driver_rate_id: siteRateSchedule.driverRate?.id ?? null,
      driver_rate_value: siteRateSchedule.driverRateValue ?? null,
      driver_rate_type: siteRateSchedule.driverRateType ?? null,
      jurisdiction: siteRateSchedule.jurisdiction ?? null,
      pw_on_site_rate: siteRateSchedule.pwOnSiteRate ?? null,
      total_fringe_rate: siteRateSchedule.totalFringeRate ?? null,
    } as unknown as SiteRateSchedule_Create;
  }

  public static deparseDTO(siteRateDTO: SiteRateScheduleDTO): SiteRateSchedule_Create {
    return {
      ...deparseCommissionFieldsDTO(siteRateDTO),
      name: siteRateDTO.name || null,
      pickup_site_id: siteRateDTO.pickupSite?.value || null,
      dropoff_site_id: siteRateDTO.dropoffSite?.value || null,
      material_id: siteRateDTO.material?.value || null,
      equipment_type_id: siteRateDTO.equipmentType?.value || null,
      quantity: numOrNull(siteRateDTO.quantity),
      customer_rate_id: null,
      customer_rate_value: numOrNull(siteRateDTO.customerRate?.rate),
      customer_rate_type: (siteRateDTO.customerRate?.type?.value as RateType) || null,
      vendor_rate_id: null,
      vendor_rate_value: numOrNull(siteRateDTO.vendorRate?.rate),
      vendor_rate_type: (siteRateDTO.vendorRate?.type?.value as RateType) || null,
      driver_rate_id: null,
      driver_rate_value: numOrNull(siteRateDTO.driverRate?.rate),
      driver_rate_type: (siteRateDTO.driverRate?.type?.value as RateType) || null,
      jurisdiction: siteRateDTO.jurisdiction || null,
      pw_on_site_rate: numOrNull(siteRateDTO.pwOnSiteRate),
      total_fringe_rate: numOrNull(siteRateDTO.totalFringeRate),
    } as unknown as SiteRateSchedule_Create;
  }

  public static deparseUpdateDTO(
    siteRateDTO: SiteRateScheduleDTO,
  ): SiteRateSchedule_Update {
    return {
      ...deparseCommissionFieldsDTO(siteRateDTO),
      name: siteRateDTO.name || null,
      pickup_site_id: siteRateDTO.pickupSite?.value || null,
      dropoff_site_id: siteRateDTO.dropoffSite?.value || null,
      material_id: siteRateDTO.material?.value || null,
      equipment_type_id: siteRateDTO.equipmentType?.value || null,
      quantity: numOrNull(siteRateDTO.quantity),
      customer_rate_id: null,
      customer_rate_value: numOrNull(siteRateDTO.customerRate?.rate),
      customer_rate_type: (siteRateDTO.customerRate?.type?.value as RateType) || null,
      vendor_rate_id: null,
      vendor_rate_value: numOrNull(siteRateDTO.vendorRate?.rate),
      vendor_rate_type: (siteRateDTO.vendorRate?.type?.value as RateType) || null,
      driver_rate_id: null,
      driver_rate_value: numOrNull(siteRateDTO.driverRate?.rate),
      driver_rate_type: (siteRateDTO.driverRate?.type?.value as RateType) || null,
      jurisdiction: siteRateDTO.jurisdiction || null,
      pw_on_site_rate: numOrNull(siteRateDTO.pwOnSiteRate),
      total_fringe_rate: numOrNull(siteRateDTO.totalFringeRate),
    } as unknown as SiteRateSchedule_Update;
  }

  public static deparseUpdate(
    siteRateSchedule: SiteRateSchedule,
  ): SiteRateSchedule_Update {
    return {
      ...deparseCommissionFields(siteRateSchedule),
      name: siteRateSchedule.name ?? null,
      pickup_site_id: siteRateSchedule.pickupSite?.id ?? null,
      dropoff_site_id: siteRateSchedule.dropoffSite?.id ?? null,
      material_id: siteRateSchedule.material?.id ?? null,
      equipment_type_id: siteRateSchedule.equipmentType?.id ?? null,
      quantity: siteRateSchedule.quantity ?? null,
      customer_rate_id: siteRateSchedule.customerRate?.id ?? null,
      customer_rate_value: siteRateSchedule.customerRateValue ?? null,
      customer_rate_type: siteRateSchedule.customerRateType ?? null,
      vendor_rate_id: siteRateSchedule.vendorRate?.id ?? null,
      vendor_rate_value: siteRateSchedule.vendorRateValue ?? null,
      vendor_rate_type: siteRateSchedule.vendorRateType ?? null,
      driver_rate_id: siteRateSchedule.driverRate?.id ?? null,
      driver_rate_value: siteRateSchedule.driverRateValue ?? null,
      driver_rate_type: siteRateSchedule.driverRateType ?? null,
      jurisdiction: siteRateSchedule.jurisdiction ?? null,
      pw_on_site_rate: siteRateSchedule.pwOnSiteRate ?? null,
      total_fringe_rate: siteRateSchedule.totalFringeRate ?? null,
    } as unknown as SiteRateSchedule_Update;
  }

  public get vendorPayCommissionPercentage() {
    return this._vendor_pay_commission_percentage;
  }
  public get driverPayCommissionPercentage() {
    return this._driver_pay_commission_percentage;
  }
  public get inheritedVendorPayCommissionPercentage() {
    return this._inherited_vendor_pay_commission_percentage;
  }
  public get inheritedDriverPayCommissionPercentage() {
    return this._inherited_driver_pay_commission_percentage;
  }
  public get netVendorRate() {
    return this._net_vendor_rate;
  }
  public get netDriverRate() {
    return this._net_driver_rate;
  }

  public get id() {
    return this._id;
  }

  public get pickupSite() {
    return this._pickup_site;
  }

  public get dropoffSite() {
    return this._dropoff_site;
  }

  public get customerRate() {
    return this._customer_rate;
  }

  public get customerRateValue() {
    return this._customer_rate_value;
  }

  public get customerRateType() {
    return this._customer_rate_type;
  }

  public get vendorRate() {
    return this._vendor_rate;
  }

  public get vendorRateValue() {
    return this._vendor_rate_value;
  }

  public get vendorRateType() {
    return this._vendor_rate_type;
  }

  public get driverRate() {
    return this._driver_rate;
  }

  public get driverRateValue() {
    return this._driver_rate_value;
  }

  public get driverRateType() {
    return this._driver_rate_type;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get name() {
    return this._name;
  }

  public get material() {
    return this._material;
  }

  public get equipmentType() {
    return this._equipment_type;
  }

  public get quantity() {
    return this._quantity;
  }

  public get jurisdiction() {
    return this._jurisdiction;
  }

  public get pwOnSiteRate() {
    return this._pw_on_site_rate;
  }

  public get totalFringeRate() {
    return this._total_fringe_rate;
  }

  /**
   * Selective rate apply: set on an order-scoped schedule that is a "pin-clone" — a snapshot holding
   * the order's rates against a skipped rate-change cascade. A pinned schedule must NOT make the order
   * read as "stamped" (see `orderOwnsSiteRates` in NewOrderForm/utils): the order stays sync-eligible.
   * Null on project cards and on real (completion) stamps.
   */
  public get pinnedAt() {
    return this._pinned_at;
  }

  /**
   * True only on an order-scoped dereference-clone — the snapshot the BE creates when a
   * project rate edit durably skips this order ("Apply rate changes to existing orders"
   * unchecked). A dereferenced order owns its rates: the FE must display and edit
   * this snapshot, never the project's live rate book. False on project cards, pin-clones,
   * and completion stamps.
   */
  public get dereferenced() {
    return this._dereferenced;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _pickup_site: Nullable<SiteBasic>,
    private _dropoff_site: Nullable<SiteBasic>,
    private _customer_rate: Nullable<RateBasicWithValue>,
    private _customer_rate_value: Nullable<number>,
    private _customer_rate_type: Nullable<RateType>,
    private _vendor_rate: Nullable<RateBasicWithValue>,
    private _vendor_rate_value: Nullable<number>,
    private _vendor_rate_type: Nullable<RateType>,
    private _driver_rate: Nullable<RateBasicWithValue>,
    private _driver_rate_value: Nullable<number>,
    private _driver_rate_type: Nullable<RateType>,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _name: Nullable<string> = null,
    private _material: Nullable<ItemNameAndId> = null,
    private _equipment_type: Nullable<ItemNameAndId> = null,
    private _quantity: Nullable<number> = null,
    private _jurisdiction: Nullable<string> = null,
    private _pw_on_site_rate: Nullable<number> = null,
    private _total_fringe_rate: Nullable<number> = null,
    private _pinned_at: Nullable<Dayjs> = null,
    private _dereferenced: boolean = false,
    private _discarded_at: Nullable<Dayjs> = null,
    private _vendor_pay_commission_percentage: NonNullable<
      SiteRateSchedule_Read['vendor_pay_commission_percentage']
    > | null = null,
    private _driver_pay_commission_percentage: NonNullable<
      SiteRateSchedule_Read['driver_pay_commission_percentage']
    > | null = null,
    private _net_vendor_rate: NonNullable<
      SiteRateSchedule_Read['net_vendor_rate']
    > | null = null,
    private _net_driver_rate: NonNullable<
      SiteRateSchedule_Read['net_driver_rate']
    > | null = null,
    private _inherited_vendor_pay_commission_percentage: NonNullable<
      SiteRateSchedule_Read['inherited_vendor_pay_commission_percentage']
    > | null = null,
    private _inherited_driver_pay_commission_percentage: NonNullable<
      SiteRateSchedule_Read['inherited_driver_pay_commission_percentage']
    > | null = null,
  ) {}
}
